import { Configuration, LogLevel } from '@azure/msal-browser'
import { AUTHORITY_DOMAIN } from './app/axios/const'
import logger from './app/middleware/log'
import { IdentityConfig } from './features/Auth/IdentityConfigService'

/**
 * MSAL configuration for the Entra External ID (CIAM) tenant.
 *
 * E7 F7-5.1: the config is no longer read from build-time REACT_APP_* env vars. MSAL cannot be
 * reconfigured after construction, so IdentityConfigLoader fetches GET /api/v1/auth/identity-config at
 * boot and calls configureAuth() with the per-Tenant result, then hands the returned Configuration to
 * PublicClientApplication. The AUTH_DISABLED docker-dev path (index.tsx) still bypasses MSAL entirely.
 */

export interface AuthError extends Error {
  // This is a short code describing the error
  errorCode: string
  // This is a descriptive string of the error,
  // and may also contain the mitigation strategy
  errorMessage: string
}

export interface LoginRequest {
  scopes: string[]
  state?: string
  extraQueryParameters?: Record<string, string>
}

// Per-Tenant scopes: openid + offline_access + the API scope from /identity-config (e.g.
// api://logikspark-dev-users-api/.default). Populated by configureAuth() before the App and its axios
// interceptors render; importers (logikspark.ts, App.tsx, RequestInterceptor) read the live binding.
export let loginRequest: LoginRequest = {
  scopes: ['openid', 'offline_access'],
  extraQueryParameters: { domainUrl: window.location.origin },
}

// Builds the MSAL Configuration from the fetched identity config. authority already includes the
// tenant path segment (External ID 400s on the bare https://<tenant>.ciamlogin.com/ form). Per Q8 the
// cache is sessionStorage (was localStorage) — consistent with the persona key + tab-close clearing the
// session.
//
// knownAuthorities must trust EVERY ciamlogin host MSAL touches while resolving the authority — NOT just
// the authority's own host. This CIAM tenant's OIDC metadata reports its `issuer` on the tenant-GUID host
// (<tenantId>.ciamlogin.com) while the authorization/token endpoints are on the vanity host
// (<subdomain>.ciamlogin.com). If the GUID host isn't trusted, MSAL's AuthorityFactory.createDiscovered
// Instance throws ClientAuthError: endpoints_resolution_error (even though the .well-known fetch itself
// succeeds), which blocks sign-in. AUTHORITY_DOMAIN is the per-env comma-separated list of BOTH hosts
// (vanity + GUID); merge it with the authority host. (Regression: the E7 F7-5.1 switch to dynamic
// identity-config replaced the AUTHORITY_DOMAIN-derived list with the single authority host, dropping the
// GUID host and orphaning AUTHORITY_DOMAIN.)
export function buildMsalConfig(config: IdentityConfig): Configuration {
  const knownAuthorities = Array.from(
    new Set(
      [new URL(config.authority).hostname, ...AUTHORITY_DOMAIN.split(',').map((h) => h.trim())].filter(Boolean),
    ),
  )
  return {
    auth: {
      clientId: config.clientId,
      authority: config.authority,
      knownAuthorities,
      // E8 F8-2: the redirect lands on the dedicated /auth/callback route. This MUST match the SPA app
      // registration's reply URL — INFRA ciam/main.tf registers only the `${origin}/auth/callback` form
      // (per-env apex + tenant wildcard), so the prior bare-origin value mismatched and Entra rejected
      // the redirect. AppShell renders /auth/callback (App's MSAL event callback completes the sign-in
      // + deep-link restore there).
      redirectUri: `${window.location.origin}/auth/callback`,
      // E8 F8-6: RP-initiated logout returns here (the anonymous /signed-out landing). Must be registered
      // as a post-logout redirect URI on the SPA app registration (INFRA ciam/main.tf) or Entra rejects it.
      postLogoutRedirectUri: `${window.location.origin}/signed-out`,
    },
    cache: {
      cacheLocation: 'sessionStorage',
    },
    system: {
      loggerOptions: {
        loggerCallback: (level: any, message: any, containsPii: any) => {
          if (containsPii === true) {
            return
          }
          switch (level) {
            case LogLevel.Error:
              logger.error(message)
              return
            case LogLevel.Info:
              logger.info(message)
              return
            case LogLevel.Verbose:
              logger.debug(message)
              return
            case LogLevel.Warning:
              logger.warn(message)
          }
        },
      },
    },
  }
}

// Sets the per-Tenant loginRequest (apiScope) + returns the MSAL Configuration. Called once by
// IdentityConfigLoader after the identity-config fetch resolves, before MSAL is constructed.
export function configureAuth(config: IdentityConfig): Configuration {
  loginRequest = {
    scopes: ['openid', 'offline_access', config.apiScope],
    state: window.location.href,
    extraQueryParameters: { domainUrl: window.location.origin },
  }
  return buildMsalConfig(config)
}

/**
 * Scopes to request when obtaining an access token for MS Graph API.
 */
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
}
